import { PageLoader, useMinLoading } from "@/components/Preloader";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { Article, Regulation } from "@/lib/types";
import { ArticleCard } from "@/components/ArticleCard";
import { ProtocolMatchCard, MentionBadges } from "@/components/sector/ProtocolMentions";
import { SearchBox, TYPE_STYLE } from "@/components/SearchSuggest";
import { EntityLogo } from "@/components/entity/EntityLink";
import { entityTrack } from "@/lib/entities";
import { Badge } from "@/components/ui/badge";

/* Search Entity Boost — strong LIVE canonical entity matches ahead of content.
   Ranking comes from the canonical resolver (exact name → exact ticker →
   alias → type priority); aliases dedupe to one card per entity id.
   Non-live entities get NO public Entity Page navigation (Rule 6). */
const EntityBoost = ({ entities }: { entities: any[] }) => {
  if (!entities.length) return null;
  return (
    <section className="mb-10" data-testid="search-entity-results">
      <h2 className="font-heading font-bold text-white text-lg mb-4">
        Entities <span className="text-gray-500 font-mono text-sm">· Canonical</span>
      </h2>
      <div className="flex flex-wrap gap-3">
        {entities.map((e: any) => (
          <Link key={e.id} to={e.route} data-testid={`search-entity-${e.type}-${e.slug}`}
            aria-label={`${e.name} — Entity Intelligence`}
            onClick={() => entityTrack("entity_nav_click", e.id, { surface: "search_entity_boost" })}
            className="group flex items-center gap-3 rounded-xl border border-white/[0.10] bg-[#0b0c10] px-4 py-3 hover:border-[#00E5FF]/50 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00E5FF]/60">
            <EntityLogo type={e.type} slug={e.slug} name={e.name} size={30} />
            <span className="min-w-0">
              <span className="flex items-center gap-1.5">
                <span className="font-bold text-[14px] text-white transition-colors duration-200 group-hover:text-[#00E5FF]">{e.name}</span>
                {e.symbol && <span className="font-mono text-[10.5px] text-gray-500">{e.symbol}</span>}
                <ArrowUpRight aria-hidden
                  className="h-3 w-3 shrink-0 text-[#00E5FF] opacity-60 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200" />
              </span>
              <span className={`inline-block mt-1 rounded border px-1.5 py-0.5 font-mono text-[7.5px] font-bold tracking-[0.16em] ${TYPE_STYLE[e.type] || TYPE_STYLE.project}`}>
                {String(e.type).toUpperCase()}{e.category ? ` · ${String(e.category).toUpperCase()}` : ""}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [input, setInput] = useState(q);

  // Live search: debounce keystrokes into the URL (replace — no history spam)
  useEffect(() => {
    const t = setTimeout(() => {
      const next = input.trim();
      if (next === q) return;
      setSearchParams(next.length >= 2 ? { q: next } : {}, { replace: true });
    }, 350);
    return () => clearTimeout(t);
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["search", q],
    queryFn: async () => (await api.get("/search", { params: { q } })).data,
    enabled: q.length >= 2,
    placeholderData: (prev: any) => prev,
  });

  const { data: entData } = useQuery({
    queryKey: ["entity-boost", q],
    queryFn: async () => (await api.get("/entities/resolve", { params: { q } })).data,
    enabled: q.length >= 2,
    staleTime: 60000,
    placeholderData: (prev: any) => prev,
  });
  const boost = useMemo(() => {
    const ordered = [entData?.resolved, ...(entData?.candidates || [])].filter(Boolean);
    const seen = new Set<string>();
    const out: any[] = [];
    for (const e of ordered) {
      if (e.live && e.route && !seen.has(e.id)) { seen.add(e.id); out.push(e); }
    }
    return out.slice(0, 4);
  }, [entData]);
  const boostProtocolSlugs = useMemo(
    () => new Set(boost.filter((e) => e.type === "protocol").map((e) => e.slug)), [boost]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim().length >= 2) setSearchParams({ q: input.trim() });
  };

  const showLoader = useMinLoading(isLoading);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="search-page">
      <h1 className="font-heading font-black text-white text-3xl sm:text-5xl mb-8">Search</h1>
      <div className="mb-10">
        <SearchBox input={input} setInput={setInput} onSubmit={submit} isFetching={isFetching} />
      </div>

      {q.length >= 2 && (
        showLoader ? (
          <PageLoader />
        ) : (
          <div data-testid="search-results">
            <EntityBoost entities={boost} />
            {!boostProtocolSlugs.has(data?.protocol_match?.slug) && <ProtocolMatchCard p={data?.protocol_match} />}
            {(data?.regulations || []).length > 0 && (
              <section className="mb-12">
                <h2 className="font-heading font-bold text-white text-lg mb-4">
                  Regulations <span className="text-gray-500 font-mono text-sm">({data.regulations.length})</span>
                </h2>
                <div className="space-y-3">
                  {data.regulations.map((r: Regulation) => (
                    <Link key={r.id} to={`/regulation/${r.slug}`}
                      className="block bg-[#121212] border border-white/10 rounded-lg p-4 hover:border-[#7C3AED]/50 transition-colors">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Badge variant="outline" className="border-[#7C3AED]/40 text-[#7C3AED] font-mono text-[10px] uppercase">
                          {r.status.replace("_", " ")}
                        </Badge>
                        <span className="font-mono text-[10px] text-gray-500">{r.countries.join(", ")}</span>
                      </div>
                      <p className="text-white font-semibold">{r.title}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
            <section>
              <h2 className="font-heading font-bold text-white text-lg mb-4">
                Articles <span className="text-gray-500 font-mono text-sm">({(data?.articles || []).length})</span>
              </h2>
              {(data?.articles || []).length === 0 ? (
                <p className="text-gray-500 py-10">No articles found for "{q}".</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {data.articles.map((a: Article) => (
                    <div key={a.id} className="relative">
                      <MentionBadges items={(a as any).protocol_mentions || []} breaking={!!a.is_breaking} />
                      <ArticleCard article={a} />
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )
      )}
      {q.length < 2 && <p className="text-gray-500">Type at least 2 characters to search the intelligence base.</p>}
    </div>
  );
}
